import React, { useEffect, useMemo, useState } from 'react';

import { dashboardPaymentSlipApis } from '@apis/next/paymentSlipApis';
import sortSvg from '@assets/img/icons/sort.svg';
import { PaymentSlipBatchFormDialog } from '@components/admin/invoice/PaymentSlipBatchFormDialog';
import { Layout } from '@components/admin/Layout';
import { SectionTitle } from '@components/admin/SectionTitle';
import { Button } from '@components/Button';
import { ExportCSVDialog } from '@components/ExportCSVDialog';
import { TagSearchInput } from '@components/Forms/TagSearchInput';
import { TagSearchInputMobxZodControl } from '@components/Forms/TagSearchInputStore';
import { PageSizeForm } from '@components/NextForms/PageSizeForm';
import { TextInput } from '@components/NextForms/TextInput';
import { PaginationWithSearchQuery } from '@components/PaginationWithSearchQuery';
import { QueryContainer } from '@components/QueryContainer';
import { cellFactory, Table } from '@components/Table/Table';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { moneyStrings } from '@data/money';
import { payeeStrings, paymentSlipStrings } from '@data/paymentSlip';
import { paymentSlipBatchStrings } from '@data/paymentSlipBatch';
import { preAlertStrings } from '@data/preAlert';
import { useSearch } from '@hooks/next/useSearch';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import {
  PaymentSlip,
  PaymentSlipHelper,
  PaymentSlipPaymentStatusName,
  PaymentSlipPaymentType,
  PaymentSlipSearchQuery,
  PaymentSlipStatusName,
} from '@models/next/PaymentSlip';
import { PaymentSlipBatchHelper } from '@models/next/PaymentSlipBatch';
import { empty } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { getFieldLabel } from '@utils/nextForms';
import { url } from '@utils/pages';
import { DEFAULT_PAGE_SIZE, formatDate, formatDateTime, toggleArray } from '@utils/types';
import { type Checklist, useChecklist } from '@utils/useChecklist';
import clsx from 'clsx';
import { pick, last } from 'remeda';

const strings = createStrings({
  ja: {
    title: `${paymentSlipStrings.payment_slip}一覧`,
    exportCSV: 'Export CSV',
    tagSearchInputPlaceholder:
      '支払番号、D2D番号、支払先や台帳番号などの検索条件を入力してください',
    details: '詳細',
    complete: '完了',
    openDeliveryPriceDialog: '金額入力',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    ...common.flexColumn,
    flex: 1,
    padding: '1.5rem 2rem',
  },
  buttonContainer: {
    ...common.flexInline,
    columnGap: '1rem',
  },
  tagSearchInputContainer: {
    ...common.middleText,
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 120px 30px) 90px 90px auto',
    rowGap: 20,
    columnGap: 8,
    marginTop: '1rem',
    marginBottom: '1rem',
  },
  paymentSlipStatusContainer: {
    ...common.flexInline,
    columnGap: '0.5rem',
  },
  paginationContainer: {
    ...common.flexColumn,
    flex: 1,
    justifyContent: 'flex-end',
    alignItems: 'flex-end',
  },
  searchTailLabel: {
    ...common.middleText,
    ...common.textGray700,
    paddingTop: 5,
  },
}));

interface PaymentSlipTableProps {
  initialIndex: number;
  items: PaymentSlip[];
  checklist: Checklist<PaymentSlip>;
}

const PaymentSlipTable: React.FC<PaymentSlipTableProps> = React.memo((props) => {
  const { initialIndex, items, checklist } = props;

  const { setChecked, toggleCheckAll, allCheckedAndNotEmpty, checked, setChecklist } = checklist;

  const { search, updateSearch } = useSearch(PaymentSlipSearchQuery);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const renderPaymentStatus = (item: PaymentSlip) => {
    const lastStatus = last(item.payment_status);

    if (!lastStatus) return <>-</>;

    const lastStatusName = lastStatus.name;

    return (
      <div
        className={clsx(
          classes.paymentSlipStatusContainer,
          classes.textError && lastStatusName === '未',
        )}
      >
        <div style={{ width: 'max-content' }}>
          <div>{lastStatusName}</div>
          <div className={classes.exSmallerText}>{formatDateTime(lastStatus.time)}</div>
        </div>
      </div>
    );
  };

  const renderStatus = (item: PaymentSlip) => {
    const lastStatus = item.status[item.status.length - 1];

    if (!lastStatus) return <>-</>;

    const lastStatusName = lastStatus.name;

    return (
      <div
        className={clsx(
          classes.paymentSlipStatusContainer,
          classes.textError && lastStatusName === '修正中',
        )}
      >
        <div style={{ width: 'max-content' }}>
          <div>{lastStatusName}</div>
          <div className={classes.exSmallerText}>{formatDateTime(lastStatus.time)}</div>
        </div>
      </div>
    );
  };

  const renderSortButton = (orderName: string) => (
    <img
      src={sortSvg}
      alt="Sort"
      className={classes.baseButton}
      style={{ position: 'relative', top: 3 }}
      onClick={() => {
        updateSearch({
          ordering: toggleArray(search.ordering ?? [], orderName),
        });
        setChecklist([]);
      }}
    />
  );

  const updateSearchOrRemove = (field: keyof PaymentSlipSearchQuery) => (v: any) => {
    updateSearch({
      page: 1,
      [field]: v ? v : undefined,
    });
    setChecklist([]);
  };

  return (
    <Table columnFixedUntil={4} fixedWidths={[60, 60, 120, 80]}>
      <Table.Head>
        <Table.Row
          columns={[
            cellFactory.createCheckable(allCheckedAndNotEmpty, toggleCheckAll),
            cellFactory.create('NO.'),
            cellFactory.create(paymentSlipStrings.payment_slip_no),
            cellFactory.createBlank(),
            cellFactory.createSelectable(
              paymentSlipStrings.payment_status,
              [
                ...PaymentSlipPaymentStatusName.options.map((option) => ({
                  label: option,
                  value: option,
                })),
                {
                  label: 'ALL',
                  value: '',
                },
              ],
              updateSearchOrRemove('payment_status'),
            ),
            cellFactory.create(payeeStrings.payee),
            cellFactory.createSelectable(
              paymentSlipStrings.payment_type,
              [
                {
                  label: PaymentSlipPaymentType.Enum.締払い,
                  value: PaymentSlipPaymentType.Enum.締払い,
                },
                {
                  label: PaymentSlipPaymentType.Enum.現払い,
                  value: PaymentSlipPaymentType.Enum.現払い,
                },
                {
                  label: 'ALL',
                  value: '',
                },
              ],
              updateSearchOrRemove('payment_type'),
            ),
            cellFactory.createSelectable(
              paymentSlipStrings.batch_no,
              [
                {
                  label: paymentSlipBatchStrings.has,
                  value: 'true',
                },
                {
                  label: paymentSlipBatchStrings.no,
                  value: 'false',
                },
                {
                  label: 'ALL',
                  value: '',
                },
              ],
              updateSearchOrRemove('has_batch_no'),
            ),
            cellFactory.createSelectable(
              paymentSlipStrings.status,
              [
                ...PaymentSlipStatusName.options.map((option) => ({
                  label: option,
                  value: option,
                })),
                {
                  label: 'ALL',
                  value: '',
                },
              ],
              updateSearchOrRemove('status'),
            ),
            cellFactory.create(bookingInquiryStrings.d2dNo),
            cellFactory.create(paymentSlipStrings.total_amount),
            cellFactory.create(paymentSlipStrings.fc_payment_slip_no),
            cellFactory.create(preAlertStrings.job_code),
            cellFactory.create(
              <>
                {paymentSlipStrings.payment_date_shimebarai} {renderSortButton('-payment_date')}
              </>,
            ),
            cellFactory.create(
              <>
                {paymentSlipStrings.deadline_shimebarai} {renderSortButton('-deadline')}
              </>,
            ),
            cellFactory.create(paymentSlipStrings.invoice_no),
            cellFactory.create(
              <>
                {paymentSlipStrings.invoice_date} {renderSortButton('-invoice_date')}
              </>,
            ),
          ]}
        />
      </Table.Head>

      <Table.Body>
        {items.map((item, i) => {
          return (
            <Table.Row
              key={item.id}
              columns={[
                cellFactory.createCheckable(checked(item), (checked) => setChecked(item, checked)),
                cellFactory.create(<>{initialIndex + i}</>),
                cellFactory.create(
                  <div className={classes.whitespacePre}>{item.payment_slip_no}</div>,
                ),
                cellFactory.create(
                  <a
                    href={
                      url('/admin/booking/PaymentSlipDetails') +
                      '?' +
                      new URLSearchParams({
                        id: String(item.id),
                        d2d_no: item.booking_inquiry.d2d_no,
                      }).toString()
                    }
                    target="_blank"
                    rel="noreferrer noopener"
                    className={classes.link}
                  >
                    {strings.details}
                  </a>,
                ),
                cellFactory.create(renderPaymentStatus(item)),
                cellFactory.create(item.payee.name_en || '-'),
                cellFactory.create(item.payment_type || '-'),
                cellFactory.create(
                  item.batch ? (
                    <a
                      href={
                        url('/admin/invoice/PaymentSlipBatchDetails') +
                        '?' +
                        new URLSearchParams({
                          id: item.batch.id.toString(),
                          batchNo: item.batch.batch_no,
                        }).toString()
                      }
                      target="_blank"
                      rel="noreferrer noopener"
                      className={classes.link}
                    >
                      {item.batch.batch_no}
                    </a>
                  ) : (
                    '-'
                  ),
                ),
                cellFactory.create(renderStatus(item)),
                cellFactory.create(
                  <div className={classes.whitespacePre}>{item.booking_inquiry.d2d_no}</div>,
                ),
                cellFactory.create(
                  <div className={classes.whitespacePre}>
                    {moneyStrings.formatYenFront(new PaymentSlipHelper(item).getTotalAmount())}
                  </div>,
                ),
                cellFactory.create(item.fc_payment_slip_no || '-'),
                cellFactory.create(item.pre_alert.job_code || '-'),
                cellFactory.create(
                  <div className={classes.whitespacePre}>{formatDate(item.payment_date)}</div>,
                ),
                cellFactory.create(formatDate(item.cutoff_date) || '-'),
                cellFactory.create(
                  <div className={classes.whitespacePre}>{item.invoice_no || '-'}</div>,
                ),
                cellFactory.create(
                  <div className={classes.whitespacePre}>{formatDate(item.invoice_date)} </div>,
                ),
              ]}
            />
          );
        })}
      </Table.Body>
    </Table>
  );
});

const PaymentSlipsSearchForm = (props: { onSubmit?: () => void }) => {
  const { search, updateSearch } = useSearch(PaymentSlipSearchQuery);

  useEffect(() => {
    updateSearch({
      ...search,
      payment_status: '未',
    });
  }, []);

  const initialOutput = useMemo(
    () => ({
      ...search,
      page_size: search.page_size ?? DEFAULT_PAGE_SIZE,
    }),
    [search],
  );

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const form = useForm(PaymentSlipSearchQuery, {
    initialOutput,
    enableReinitialize: true,
  });

  const { fields } = form.root;

  const controller = useMemo(
    () =>
      new TagSearchInputMobxZodControl(
        pick(fields, [
          'd2d_no',
          'payment_slip_no',
          'payee',
          'payment_type',
          'payment_status',
          'job_code',
          'batch_no',
        ]),
        form,
      ),
    [fields],
  );

  return (
    <form
      {...form.bindForm({
        onSubmit(d) {
          updateSearch({ ...d, page: 1 });
          props.onSubmit?.();
        },
      })}
      className={classes.tagSearchInputContainer}
    >
      <TagSearchInput
        controller={controller}
        placeholder={strings.tagSearchInputPlaceholder}
        styles={{
          wrapper: {
            gridColumn: '1 / -1',
          },
        }}
      />

      <TextInput
        field={fields.payment_date_after}
        inputProps={{ placeholder: getFieldLabel(fields.payment_date_after) }}
      />

      <label className={classes.searchTailLabel}>{formStrings.from}</label>

      <TextInput
        field={fields.payment_date_before}
        inputProps={{ placeholder: getFieldLabel(fields.payment_date_before) }}
      />

      <label className={classes.searchTailLabel}>{formStrings.until}</label>

      <TextInput
        field={fields.cutoff_date_after}
        inputProps={{ placeholder: getFieldLabel(fields.cutoff_date_after) }}
      />

      <label className={classes.searchTailLabel}>{formStrings.from}</label>

      <TextInput
        field={fields.cutoff_date_before}
        inputProps={{ placeholder: getFieldLabel(fields.cutoff_date_before) }}
      />

      <label className={classes.searchTailLabel}>{formStrings.until}</label>

      <Button text={formStrings.filter} color="primary" type="submit" size="small" shape="round" />
      <Button
        color="primary"
        shape="round"
        text={formStrings.reset}
        type="submit"
        size="small"
        onClick={() => {
          form.root.setOutput(empty);
        }}
      />
    </form>
  );
};

export const PaymentSlipList: React.FC = () => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const { search: _search } = useSearch(PaymentSlipSearchQuery);

  const search = {
    ..._search,
    page: _search.page ?? 1,
    page_size: _search.page_size ?? DEFAULT_PAGE_SIZE,
  } satisfies PaymentSlipSearchQuery;

  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const [batchDialogOpen, setBatchDialogOpen] = useState(false);

  const paymentSlipsQuery = dashboardPaymentSlipApis.useGet('/dashboard/payment-slip', {
    queries: search,
    page: search.page ?? 1,
    page_size: search.page_size ?? DEFAULT_PAGE_SIZE,
  });

  const paymentSlipsTableChecklist = useChecklist([], paymentSlipsQuery.data?.results ?? []);

  const confirmDialog = useConfirmDialog();

  const canCreateBatch = PaymentSlipBatchHelper.canCreateBatch(
    paymentSlipsTableChecklist.checklist,
  );

  const initialPaymentSlip = useMemo(
    () => PaymentSlipBatchHelper.resolveBatchFromPaymentSlips(paymentSlipsTableChecklist.checklist),
    [paymentSlipsTableChecklist.checklist],
  );

  return (
    <Layout title={strings.title}>
      <PaymentSlipBatchFormDialog
        open={batchDialogOpen}
        onClose={() => setBatchDialogOpen(false)}
        onSuccess={() => paymentSlipsTableChecklist.setChecklist([])}
        initialPaymentSlipBatch={initialPaymentSlip}
        type="create"
      />
      <ExportCSVDialog
        open={exportDialogOpen}
        onClose={() => {
          setExportDialogOpen(false);
        }}
        subject={paymentSlipStrings.payment_slip}
        baseUrl="/dashboard/payment-slip/"
        query={search}
        selectedIds={paymentSlipsTableChecklist.checklist.map((item) => item.id)}
      />

      {confirmDialog.renderDialog()}

      <div className={classes.container}>
        <SectionTitle
          actions={
            <div className={classes.buttonContainer}>
              <Button
                color="primary"
                shape="round"
                text={paymentSlipBatchStrings.createBatch}
                onClick={async () => {
                  if (canCreateBatch?._tag !== 'Right') {
                    await confirmDialog.confirm({
                      title: formStrings.error,
                      message: canCreateBatch?.left.messages.join('\n'),
                    });

                    return;
                  }
                  setBatchDialogOpen(true);
                }}
              />
              <Button
                color="primary"
                shape="round"
                text={strings.exportCSV}
                onClick={() => setExportDialogOpen(true)}
              />
            </div>
          }
        >
          {strings.title}
        </SectionTitle>

        <PaymentSlipsSearchForm onSubmit={() => paymentSlipsTableChecklist.setChecklist([])} />

        <PageSizeForm />

        <div style={{ height: 16 }} />

        <QueryContainer queries={[paymentSlipsQuery]}>
          <PaymentSlipTable
            initialIndex={1}
            items={paymentSlipsQuery.data?.results ?? []}
            checklist={paymentSlipsTableChecklist}
          />

          <div className={classes.paginationContainer}>
            <PaginationWithSearchQuery query={paymentSlipsQuery} />
          </div>
        </QueryContainer>
      </div>
    </Layout>
  );
};
